import React, { useState, useRef, useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChevronUp, Eye, EyeOff } from 'feather-icons-react/build/IconComponents';
import { setToogleHeader } from '../../core/redux/action';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { PlusCircle } from 'react-feather';
import Table from '../../core/pagination/datatable';
import * as bootstrap from "bootstrap";
import { apiClient } from "../../services/apiClient";
import { useToast } from '../../Toaster/ToastContext';
import Loader from '../loader/loader';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2'

const CreateBusinesses = () => {

    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        status: 'Active',
        password: '',
        confirm_password: ''
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();
    const hasFetched = useRef(false);
    const [businesses, setBusiness] = useState([]);
    const [filteredBusinesses, setFilteredBusinesses] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    const renderCollapseTooltip = (props) => (
        <Tooltip id="refresh-tooltip" {...props}>
            Collapse
        </Tooltip>
    )

    const handleAdd = () => {
        // Reset form data when adding a new business
        setFormData({
            name: '',
            email: '',
            phone: '',
            status: 'Active',
            password: '',
            confirm_password: ''
        });
        setErrors({});
        setIsEditing(false);
        setEditId(null);
        
        const modalElement = document.getElementById('add-units');
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
    };

    const handleEdit = (record) => {
        const modalElement = document.getElementById('add-units');
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
        setFormData({
            name: record.name || '',
            email: record.email || '',
            phone: record.phone || '',
            status: record.status === 'active' ? 'Active' : 'Inactive',
            password: '', // Clear password fields for security
            confirm_password: ''
        });
        setIsEditing(true);
        setEditId(record.id);
        setErrors({});
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Business Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
        if (!formData.password) newErrors.password = 'Password is required';
        if (!formData.confirm_password) newErrors.confirm_password = 'Confirm Password is required';
        else if (formData.password !== formData.confirm_password) newErrors.confirm_password = 'Passwords do not match';
        return newErrors;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        
        // Skip password validation if editing
        if (isEditing) {
            delete validationErrors.password;
            delete validationErrors.confirm_password;
        }
        
        if (Object.keys(validationErrors).length === 0) {
            // Prepare data for API
            const apiPayload = {
                ...formData,
                id: editId // Include ID if editing
            };
            
            // If editing and passwords are empty, remove them from payload
            if (isEditing && !formData.password) {
                delete apiPayload.password;
                delete apiPayload.confirm_password;
            }
            
            const apiData = await apiClient.post('/store-update-business', apiPayload);
            if(apiData.success){
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    status: 'Active',
                    password: '',
                    confirm_password: ''
                });
                setIsEditing(false);
                setEditId(null);
                hasFetched.current = false;
                setErrors({});
                showToast(apiData.message, 'success', 'Business Operation!');
                const modalElement = document.getElementById('add-units');
                const modal = bootstrap.Modal.getInstance(modalElement);
                modal.hide();
                await fetchBusiness();
            }
            else{
                showToast(apiData.message, 'error', 'Business Operation!');
            }
        } else {
            setErrors(validationErrors);
        }
    };

    useEffect(() => {
        fetchBusiness();
    }, []);

    const fetchBusiness = async () => {
        if (hasFetched.current) return;
        setLoading(true);
        try {
            const response = await apiClient.post('/get-all-business' ,{
                pageIndex: 0,
                pageSize: 50,
                previousPageIndex: 0,
            });
            if (response.success) {
             setBusiness(response.data);
             setFilteredBusinesses(response.data);
         } else {
             setBusiness([]);
             setFilteredBusinesses([]);
            }
        } catch (error) {
            showToast(error.message || 'Failed to fetch expenses', 'error', 'Expenses');
        } finally {
            setLoading(false);
            hasFetched.current = true;
        }
    };

    const columns = [

        {
            title: "Business Name",
            dataIndex: "name",
            sorter: (a, b) => a.name.length - b.name.length,
        },

        {
            title: "Phone",
            dataIndex: "phone",
            sorter: (a, b) => a.phone.length - b.phone.length,
        },
        {
            title: "Email",
            dataIndex: "email",
            sorter: (a, b) => a.email.length - b.email.length,
        },
        {
            title: "Created On",
            dataIndex: "created_at",
            sorter: (a, b) => a.created_at.length - b.created_at.length,
            render: (text) => {
                const date = new Date(text);
                return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }).split('/').join('-');
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text) => (
                <span className={`badge ${text === 'active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
                    {text === 'active' ? 'Active' : 'Inactive'}
                </span>
            ),
            sorter: (a, b) => a.status.length - b.status.length,
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            render: (text, record) => (
                <td className="action-table-data">
                    <div className="edit-delete-action">
                        <Link className="me-2 p-2" to="#" onClick={() => handleEdit(record)}>
                            <i data-feather="edit" className="feather-edit"></i>
                        </Link>
                        <Link className="confirm-text p-2" to="#" onClick={() => showConfirmationAlert(record)} >
                            <i data-feather="trash-2" className="feather-trash-2"></i>
                        </Link>
                    </div>
                </td>
            )
        },
    ]

    const MySwal = withReactContent(Swal);

    const showConfirmationAlert = (record) => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You won\'t be able to revert this!',
            showCancelButton: true,
            confirmButtonColor: '#00ff00',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#ff0000',
            cancelButtonText: 'Cancel',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    const response = await apiClient.post('/delete-business', {
                        id: record.id,
                    });
                    if (response.success) {
                        showToast('Business deleted successfully', 'success', 'Business Operation!');
                        hasFetched.current = false;
                        fetchBusiness();
                    } else {
                        showToast(response.message || 'Failed to delete business', 'error', 'Business Operation!');
                    }
                } catch (error) {
                    showToast(error.message || 'Failed to delete business', 'error', 'Business Operation!');
                } finally {
                    setLoading(false);
                }
            } else {
                MySwal.close();
            }

        });
    };

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setFilteredBusinesses(
            businesses.filter((business) =>
                business.name.toLowerCase().includes(term) ||
                business.status.toLowerCase().includes(term)
            )
        );
    };

    return (
        <div>
            <Loader loading={loading} />
            <div className="page-wrapper" style={{ position: 'relative', minHeight: '100vh' }}>
                <div className="content">
                    <div className="page-header">
                        <div className="add-item d-flex">
                            <div className="page-title">
                                <h4>Business List</h4>
                                <h6>Manage Your Businesses</h6>
                            </div>
                        </div>

                        <ul className="table-top-head">
                            <li>
                                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                                    <Link
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="top"
                                        id="collapse-header"
                                        className={data ? "active" : ""}
                                        onClick={() => { dispatch(setToogleHeader(!data)) }}
                                    >
                                        <ChevronUp />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                        </ul>
                        <div className="page-btn">
                            <a
                                to="#"
                                className="btn btn-added"
                                onClick={handleAdd}
                            >
                                <PlusCircle className="me-2" />
                                Add New Business
                            </a>
                        </div>
                    </div>
                    {/* /product list */}
                    <div className="card table-list-card">
                        <div className="card-body">
                            <div className="table-top">
                                <div className="search-set">
                                    <div className="search-input">
                                        <input
                                            type="text"
                                            placeholder="Search"
                                            className="form-control form-control-sm formsearch"
                                            onChange={handleSearch}
                                        />
                                        <Link to className="btn btn-searchset">
                                            <i data-feather="search" className="feather-search" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <Table 
                                    columns={columns}
                                    dataSource={filteredBusinesses}
                                    rowSelection={null}
                                    rowKey={(record) => record.id}
                                />

                            </div>
                        </div>
                    </div>
                    {/* /product list */}
                </div>
            </div>

            {/* Add Business */}
            <div className="modal fade" id="add-units">
                <div className="modal-dialog modal-dialog-centered custom-modal-two">
                    <div className="modal-content">
                        <div className="page-wrapper-new p-0">
                            <div className="content">
                                <div className="modal-header border-0 custom-modal-header d-flex justify-content-between align-items-center">
                                    <div className="page-title">
                                        <h4>Add Business</h4>
                                    </div>
                                    <button type="button" className="close" data-bs-dismiss="modal" aria-label="Close">
                                        <span aria-hidden="true">×</span>
                                    </button>
                                </div>
                                <div className="modal-body custom-modal-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row">
                                            <div className="col-lg-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Business Name</label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Business Name"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                    />
                                                    {errors.name && (
                                                        <div className="invalid-feedback">{errors.name}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-lg-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Email</label>
                                                    <input
                                                        type="email"
                                                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                    />
                                                    {errors.email && (
                                                        <div className="invalid-feedback">{errors.email}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-lg-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Phone Number</label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Phone Number"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleInputChange}
                                                    />
                                                    {errors.phone && (
                                                        <div className="invalid-feedback">{errors.phone}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-lg-6">
                                                <div className="mb-3">
                                                    <label className="form-label">Status</label>
                                                    <select
                                                        className="form-select"
                                                        name="status"
                                                        value={formData.status}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option>Active</option>
                                                        <option>Inactive</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="col-lg-6">
                                                <div className="mb-3 position-relative">
                                                    <label className="form-label">Password</label>
                                                    <input
                                                        type={showPassword ? 'text' : 'password'}
                                                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Password"
                                                        name="password"
                                                        value={formData.password}
                                                        onChange={handleInputChange}
                                                    />
                                                    <span
                                                        className="position-absolute"
                                                        style={{ right: '10px', top: '38px', cursor: 'pointer' }}
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    >
                                                        {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                                                    </span>
                                                    {errors.password && (
                                                        <div className="invalid-feedback">{errors.password}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-lg-6">
                                                <div className="mb-3 position-relative">
                                                    <label className="form-label">Confirm Password</label>
                                                    <input
                                                        type={showConfirmPassword ? 'text' : 'password'}
                                                        className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                                                        placeholder="Enter Confirm Password"
                                                        name="confirm_password"
                                                        value={formData.confirm_password}
                                                        onChange={handleInputChange}
                                                    />
                                                    <span
                                                        className="position-absolute"
                                                        style={{ right: '10px', top: '38px', cursor: 'pointer' }}
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    >
                                                        {showConfirmPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                                                    </span>
                                                    {errors.confirmPassword && (
                                                        <div className="invalid-feedback">{errors.confirmPassword}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="modal-footer-btn">
                                            <button
                                                type="button"
                                                className="btn btn-cancel me-2"
                                                data-bs-dismiss="modal"
                                            >
                                                Cancel
                                            </button>
                                            <button type="submit" className="btn btn-submit">
                                                Submit
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Add Business */}
        </div>
    )

}
export default CreateBusinesses
